import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, RefreshCw, CheckCircle2, XCircle, ShieldCheck, ShieldAlert } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * HealthzStatusCard — compact live-updating admin card fed by GET /api/healthz.
 * Different from SystemHealthCard (which lives on /admin/settings and covers
 * production-readiness deep-checks). This one is a 30-second ops dashboard
 * pill for /admin/dashboard.
 */
export function HealthzStatusCard() {
  const [state, setState] = useState({ loading: true, ok: null, checks: null, error: null, at: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      // Use raw axios (no auth interceptor) — /api/healthz is public.
      const { data, status } = await axios.get(`${API}/healthz`, { timeout: 8000, validateStatus: () => true });
      setState({ loading: false, ok: status === 200 && data?.ok, checks: data?.checks || null, error: null, at: new Date() });
    } catch (e) {
      setState({ loading: false, ok: false, checks: null, error: e.message || "unreachable", at: new Date() });
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const c = state.checks || {};
  const dot = (ok) => ok
    ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
    : <XCircle className="h-4 w-4 text-red-400 shrink-0" />;

  return (
    <Card className="border-white/10 bg-card p-5" data-testid="healthz-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline flex items-center gap-1">
            <Activity className="h-3 w-3" /> Live probe
          </div>
          <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
            System health
            {state.loading ? (
              <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px] uppercase tracking-widest" data-testid="healthz-pill-loading">checking…</Badge>
            ) : state.ok ? (
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[10px] uppercase tracking-widest" data-testid="healthz-pill-ok">All systems go</Badge>
            ) : (
              <Badge variant="outline" className="border-red-500/40 text-red-300 bg-red-500/10 text-[10px] uppercase tracking-widest" data-testid="healthz-pill-down">Degraded</Badge>
            )}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {state.at ? `Refreshed ${state.at.toLocaleTimeString()}` : "Never refreshed"} · Auto-polls every 30s.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={state.loading} className="border-white/10 h-8" data-testid="healthz-refresh">
          <RefreshCw className={"h-3 w-3 mr-1 " + (state.loading ? "animate-spin" : "")} /> Recheck
        </Button>
      </div>

      {state.error && (
        <div className="text-xs text-red-300 mb-3 rounded-md border border-red-500/20 bg-red-500/5 p-2" data-testid="healthz-error">
          Cannot reach /api/healthz: {state.error}
        </div>
      )}

      {!state.error && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-center gap-2" data-testid="healthz-mongo">
            {dot(c.mongo?.ok)}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium">MongoDB</div>
              <div className="text-[10px] text-zinc-500 truncate">{c.mongo?.ok ? "Reachable" : (c.mongo?.error || "down")}</div>
            </div>
          </div>
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-center gap-2" data-testid="healthz-stripe">
            {dot(c.stripe?.ok)}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium">Stripe</div>
              <div className="text-[10px] text-zinc-500 truncate">mode: {c.stripe?.mode || "—"}</div>
            </div>
          </div>
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-center gap-2" data-testid="healthz-llm">
            {dot(c.llm_key?.ok)}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium">LLM Key</div>
              <div className="text-[10px] text-zinc-500 truncate">{c.llm_key?.ok ? "Emergent key present" : "missing"}</div>
            </div>
          </div>
        </div>
      )}

      {/* Security posture */}
      {c.security_config && (
        <div className="rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid="healthz-security">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
            {c.security_config.sentry_env === "production" ? <ShieldCheck className="h-3 w-3 text-emerald-400" /> : <ShieldAlert className="h-3 w-3 text-amber-400" />}
            Security posture · {c.security_config.sentry_env || "unset"}
          </div>
          <div className="flex flex-wrap gap-2 text-[11px]">
            <SecPill on={c.security_config.rate_limit_enabled} label="Rate limit" testid="healthz-sec-rl" />
            <SecPill on={c.security_config.auth_rate_limit_enabled} label="Auth throttle" testid="healthz-sec-arl" />
            <SecPill on={c.security_config.webhook_strict_mode} label="Webhook strict" testid="healthz-sec-ws" />
          </div>
        </div>
      )}
    </Card>
  );
}

function SecPill({ on, label, testid }) {
  return (
    <span
      data-testid={testid}
      className={
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 border " +
        (on
          ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
          : "border-amber-500/40 text-amber-300 bg-amber-500/10")
      }
    >
      <span className={"h-1.5 w-1.5 rounded-full " + (on ? "bg-emerald-400" : "bg-amber-400")} />
      {label} {on ? "on" : "off"}
    </span>
  );
}
